import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CreditCard, Lock, RefreshCw, ShieldCheck } from 'lucide-react';
import { adminListPaymentMethods } from '@/services/realtimeApi';
import { METHOD_BY_KEY, METHOD_TYPES, STATUS_BY_KEY } from '@/components/payment-methods/methodConfig';

const AdminProviderPaymentMethodsPage = () => {
  const { getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ type: 'all', country: '', status: 'active', q: '' });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.type !== 'all') params.type = filters.type;
      if (filters.country)        params.country = filters.country.toUpperCase();
      if (filters.status !== 'all') params.status = filters.status;
      const r = await adminListPaymentMethods(getAuthHeaders, params);
      setItems(r.items || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.type, filters.country, filters.status]);

  const filtered = useMemo(() => {
    if (!filters.q) return items;
    const t = filters.q.toLowerCase();
    return items.filter((m) => (
      (m.label || '').toLowerCase().includes(t) ||
      (m._provider?.name || '').toLowerCase().includes(t) ||
      (m.country || '').toLowerCase().includes(t)
    ));
  }, [items, filters.q]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((m) => {
      const k = m.provider_id;
      if (!g[k]) g[k] = { provider: m._provider, methods: [] };
      g[k].methods.push(m);
    });
    return Object.entries(g).sort((a, b) => (a[1].provider?.name || '').localeCompare(b[1].provider?.name || ''));
  }, [filtered]);

  return (
    <div className="space-y-6" data-testid="admin-payment-methods-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-indigo-600" />
            Métodos de Pago de Proveedores
          </h1>
          <p className="text-xs text-slate-500 mt-1">Catálogo completo agrupado por proveedor. Vista de solo lectura.</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-slate-50 border-slate-200 text-slate-700 gap-1">
            <Lock className="w-3 h-3" /> Solo lectura
          </Badge>
          <Badge variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-700 gap-1">
            <ShieldCheck className="w-3 h-3" /> Provider isolation
          </Badge>
          <Button size="sm" variant="outline" onClick={load} data-testid="apm-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Refrescar
          </Button>
        </div>
      </div>

      <Card className="border-0 shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-slate-500 font-medium">Buscar</label>
            <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="nombre / proveedor / país" data-testid="apm-search" />
          </div>
          <div>
            <label className="text-xs text-slate-500 font-medium">Tipo</label>
            <Select value={filters.type} onValueChange={(v) => setFilters({ ...filters, type: v })}>
              <SelectTrigger data-testid="apm-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {METHOD_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500 font-medium">País (ISO-2)</label>
            <Input value={filters.country} onChange={(e) => setFilters({ ...filters, country: e.target.value.toUpperCase() })} placeholder="UY / CU" maxLength={2} data-testid="apm-country" />
          </div>
          <div>
            <label className="text-xs text-slate-500 font-medium">Estado</label>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger data-testid="apm-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="active">Activo</SelectItem>
                <SelectItem value="paused">Pausado</SelectItem>
                <SelectItem value="archived">Archivado</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="flex justify-center p-12"><div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : grouped.length === 0 ? (
        <Card className="border-0 shadow-sm p-12 text-center text-slate-500">
          <CreditCard className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p>Sin métodos para mostrar</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {grouped.map(([pid, { provider, methods }]) => (
            <Card key={pid} className="border-0 shadow-sm p-4" data-testid={`apm-provider-group-${pid}`}>
              <div className="flex items-center justify-between mb-3 pb-2 border-b">
                <div>
                  <p className="font-bold text-slate-900">{provider?.name || pid}</p>
                  <p className="text-[10px] uppercase text-slate-500">{provider?.role === 'payment_agent' ? 'Agente de pago' : 'Proveedor de tickets'} · {methods.length} método(s)</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {methods.map((m) => {
                  const t = METHOD_BY_KEY[m.type] || { label: m.type, icon: CreditCard };
                  const s = STATUS_BY_KEY[m.status] || { label: m.status, color: 'bg-slate-100 text-slate-700' };
                  const Ic = t.icon;
                  return (
                    <div key={m.id} className="border rounded-lg p-3 bg-slate-50/40" data-testid={`apm-method-${m.id}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <Ic className="w-4 h-4 text-blue-600 shrink-0" />
                          <p className="font-semibold text-sm text-slate-900 truncate">{m.label}</p>
                        </div>
                        <Badge variant="outline" className={`text-[10px] font-bold border ${s.color}`}>{s.label}</Badge>
                      </div>
                      <p className="text-[11px] text-slate-500">{t.label} · {m.country}</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {(m.currencies || []).map((c) => (
                          <span key={c} className="px-1.5 py-0.5 bg-white border rounded text-[10px] font-mono">{c}</span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminProviderPaymentMethodsPage;
